import { User, Mail, Phone, Loader2 } from 'lucide-react';
import React, { useState } from 'react';

import { useOnboardingStore } from '../../../store/onboarding.store';
import { saveCompanyData } from '../../../utils/saveCompanyData';

interface ContactInfoStepProps {
  onNext: () => void;
  onBack: () => void;
}

export function ContactInfoStep({ onNext, onBack }: ContactInfoStepProps) {
  const { companyData, setCompanyData, companyId, setCompanyId } = useOnboardingStore();
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const isValidEmail = (email: string) => {
    const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
    return emailRegex.test(email);
  };

  const isValidPhone = (phone: string) => {
    const digitsOnly = phone.replace(/\D/g, '');
    return digitsOnly.length >= 10;
  };

  const formatPhone = (value: string) => {
    const digits = value.replace(/\D/g, '');
    if (digits.length <= 3) return digits;
    if (digits.length <= 6) return `(${digits.slice(0, 3)}) ${digits.slice(3)}`;
    return `(${digits.slice(0, 3)}) ${digits.slice(3, 6)}-${digits.slice(6, 10)}`;
  };

  const canContinue =
    (companyData.contactFirstName || '').trim() &&
    (companyData.contactLastName || '').trim() &&
    isValidEmail(companyData.email || '') &&
    isValidPhone(companyData.phone || '');

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!canContinue) return;

    setSaving(true);
    setError(null);

    try {
      // Save incrementally - UPDATE if companyId exists, CREATE if not
      const result = await saveCompanyData(companyId, {
        name: companyData.name,
        contactFirstName: companyData.contactFirstName || null,
        contactLastName: companyData.contactLastName || null,
        email: companyData.email || null,
        phone: companyData.phone || null,
      });

      // Store companyId if it was just created
      if (result.created && result.companyId) {
        setCompanyId(result.companyId);
      }

      onNext();
    } catch (err) {
      console.error('[ContactInfoStep] Save error:', err);
      setError(err instanceof Error ? err.message : 'Failed to save. Please try again.');
    } finally {
      setSaving(false);
    }
  };

  return (
    <form onSubmit={handleSubmit} className="space-y-6">
      <div className="text-center mb-8">
        <div className="w-16 h-16 rounded-full bg-[#9B1421]/20 flex items-center justify-center mx-auto mb-4">
          <User size={32} className="text-rollops-accent" />
        </div>
        <h2 className="text-2xl font-bold text-[#EBFFEE]">Primary Business Contact</h2>
        <p className="text-[#A9A3A3] mt-2">Owner and primary business contact person for your business entity</p>
        <p className="text-xs text-[#706A6A] mt-1">Note: Location-specific contact information will be collected in the Location section</p>
      </div>

      {/* Contact Name */}
      <div className="grid grid-cols-2 gap-4">
        <div className="space-y-2">
          <label className="block text-sm font-medium text-[#EBFFEE]">
            First Name <span className="text-rollops-accent">*</span>
          </label>
          <input
            type="text"
            value={companyData.contactFirstName || ''}
            onChange={(e) => setCompanyData({ contactFirstName: e.target.value })}
            placeholder="John"
            className="w-full px-4 py-3 rounded-lg bg-[#504A4A] text-[#EBFFEE] placeholder:text-[#A9A3A3] border-none outline-none focus:ring-2 focus:ring-[#9B1421]/50 transition-all"
            required
            maxLength={30}
          />
        </div>
        <div className="space-y-2">
          <label className="block text-sm font-medium text-[#EBFFEE]">
            Last Name <span className="text-rollops-accent">*</span>
          </label>
          <input
            type="text"
            value={companyData.contactLastName || ''}
            onChange={(e) => setCompanyData({ contactLastName: e.target.value })}
            placeholder="Doe"
            className="w-full px-4 py-3 rounded-lg bg-[#504A4A] text-[#EBFFEE] placeholder:text-[#A9A3A3] border-none outline-none focus:ring-2 focus:ring-[#9B1421]/50 transition-all"
            required
            maxLength={30}
          />
        </div>
      </div>

      {/* Email */}
      <div className="space-y-2">
        <label className="block text-sm font-medium text-[#EBFFEE]">
          <div className="flex items-center gap-2">
            <Mail size={16} />
            Business Email <span className="text-rollops-accent">*</span>
          </div>
        </label>
        <input
          type="email"
          value={companyData.email || ''}
          onChange={(e) => setCompanyData({ email: e.target.value })}
          placeholder="contact@yourgym.com"
          className={`w-full px-4 py-3 rounded-lg bg-[#504A4A] text-[#EBFFEE] placeholder:text-[#A9A3A3] border-2 outline-none focus:ring-2 focus:ring-[#9B1421]/50 transition-all ${
            companyData.email && !isValidEmail(companyData.email)
              ? 'border-red-500'
              : 'border-transparent'
          }`}
          required
          maxLength={254}
        />
        {companyData.email && !isValidEmail(companyData.email) && (
          <p className="text-xs text-red-500">Please enter a valid email address</p>
        )}
      </div>

      {/* Phone */}
      <div className="space-y-2">
        <label className="block text-sm font-medium text-[#EBFFEE]">
          <div className="flex items-center gap-2">
            <Phone size={16} />
            Business Phone <span className="text-rollops-accent">*</span>
          </div>
        </label>
        <input
          type="tel"
          value={companyData.phone || ''}
          onChange={(e) => setCompanyData({ phone: formatPhone(e.target.value) })}
          placeholder="(555) 123-4567"
          className={`w-full px-4 py-3 rounded-lg bg-[#504A4A] text-[#EBFFEE] placeholder:text-[#A9A3A3] border-2 outline-none focus:ring-2 focus:ring-[#9B1421]/50 transition-all ${
            companyData.phone && !isValidPhone(companyData.phone)
              ? 'border-red-500'
              : 'border-transparent'
          }`}
          required
        />
        {companyData.phone && !isValidPhone(companyData.phone) && (
          <p className="text-xs text-red-500">Please enter a valid phone number</p>
        )}
      </div>

      {error && (
        <div className="bg-red-900/20 border border-red-500/50 rounded-lg p-3">
          <p className="text-red-400 text-sm text-center">{error}</p>
        </div>
      )}

      {/* Navigation Buttons */}
      <div className="flex gap-4">
        <button
          type="button"
          onClick={onBack}
          disabled={saving}
          className="flex-1 py-3 px-4 bg-[#504A4A] hover:bg-[#5A5454] disabled:opacity-50 text-white font-semibold rounded-lg transition-colors"
        >
          Back
        </button>
        <button
          type="submit"
          disabled={!canContinue || saving}
          className="flex-1 py-3 px-4 bg-[#9B1421] hover:bg-[#7A0F1A] disabled:bg-[#504A4A] disabled:cursor-not-allowed text-white font-semibold rounded-lg transition-colors flex items-center justify-center gap-2"
        >
          {saving ? (
            <>
              <Loader2 size={18} className="animate-spin" />
              Saving...
            </>
          ) : (
            'Continue'
          )}
        </button>
      </div>
    </form>
  );
}

